import findIndexByInstanceKey from './findIndexByInstanceKey';
import findIndexToInsertAt from './findIndexToInsertAt';
import getDateSortTimeStampFromRowData from 'owa-mail-list-response-processor/lib/actions/helpers/getDateSortTimeStampFromRowData';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import removeRowsFromListViewStore from '../../triage/removeRowsFromListViewStore';
import setRowOperation from './setRowOperation';
import shouldSkipProcessRow from 'owa-mail-list-response-processor/lib/actions/helpers/shouldSkipProcessRow';
import TableOperations from 'owa-mail-list-table-operations';
import tombstoneOperations from 'owa-mail-list-tombstone';
import updateRollupOnAddOrUpdateRowNotification from 'owa-mail-focused-inbox-rollup-store/lib/actions/updateRollupOnAddOrUpdateRowNotification';
import { action } from 'satcheljs/lib/legacy';
import { attachmentPreviewOperations } from 'owa-mail-attachment-previews';
import { getMailboxInfo } from 'owa-mail-mailboxinfo';
import { isRowSortKeyEqual } from 'owa-mail-list-response-processor/lib/actions/helpers/isSortKeyEqual';
import { lazyPrefetchSingleRow } from 'owa-mail-prefetch';
import { getFocusedFilterForTable, getIsMailNotificationRowPinned, } from 'owa-mail-list-store';
import { lazyFetchCouponData } from 'owa-mail-coupon-peek';
import { isControlFeatureEnabled } from 'owa-controls-feature-flags';
var arrayEquals = require('array-equal');
/**
 * Performs update for a row that already exists on the table
 * @param row being updated
 * @param tableView where the row is updated
 * @param indexToInsertAt the index to insert the row at
 * @param currentIndex the current index of the row in the table
 */
function updateExistingRow(row, tableView, indexToInsertAt, currentIndex) {
    var isSortKeyEqual = isRowSortKeyEqual(row, tableView);
    if (isSortKeyEqual) {
        // Only update data if sort key is the same
        TableOperations.updateRowData(row, tableView);
        setRowOperation(tableView, 4 /* RowUpdateInPlace */);
    }
    else {
        // Update data and row position otherwise
        TableOperations.updateRow(indexToInsertAt, row, tableView);
        setRowOperation(tableView, 2 /* RowMove */);
    }
}
/**
 * Inserts a new row that does not exist on the table
 * @param row being updated
 * @param tableView where the row is updated
 * @param indexToInsertAt - the index to insert the row at
 */
function addNewRow(row, tableView, indexToInsertAt) {
    TableOperations.addRow(indexToInsertAt, row, tableView);
    setRowOperation(tableView, 1 /* RowAdd */);
    // Also increment current loaded index so the new row will be guranteed to show up in the view
    // without bumping out an existing item
    tableView.currentLoadedIndex++;
}
/**
 * Check and remove rows from the tombstone based on current notification if it matches criteria
 * Return a flag indicating whether to process the payload further
 * @param row from notification
 * @param tableView where the row is updated
 */
function preProcessRowNotificationPayload(row, tableView) {
    var removeFromTombstoneCondition = tombstoneOperations.contains(row.InstanceKey, tableView.serverFolderId);
    if (!removeFromTombstoneCondition) {
        // The row is not tombstoned, no need to continue
        return true;
    }
    var isRowPinned = getIsMailNotificationRowPinned(row, tableView.tableQuery.listViewType);
    switch (removeFromTombstoneCondition.type) {
        case 1 /* OnPinNotification */:
            // VSO: 30489 - Apply property's local value from the tombstone map
            // when we receive row notifications for this row for non-delete operations
            if (isRowPinned) {
                // Row is being pinned, matching tombstone remove condition, so remove it now
                tombstoneOperations.remove(row.InstanceKey, tableView.serverFolderId);
            }
            return false;
        case 2 /* OnUnpinNotification */:
            // VSO: 30489 - Apply property's local value from the tombstone map
            // when we receive row notifications for this row for non-delete operations
            if (!isRowPinned) {
                // Row is being unpinned, matching tombstone remove condition, so remove it now
                tombstoneOperations.remove(row.InstanceKey, tableView.serverFolderId);
            }
            return false;
        case 3 /* OnEqualCategories */:
            // Quickly compare if two categories array are of equal value, even though the order is different.
            // slice() clones a copy so sort() will not mess up with the original categories
            var localCategoryValue = removeFromTombstoneCondition.value
                ? removeFromTombstoneCondition.value.slice().sort()
                : [];
            var rowPayloadCategoryValue = row.Categories ? row.Categories.slice().sort() : [];
            if (arrayEquals(rowPayloadCategoryValue, localCategoryValue)) {
                // Row has the matching set of categories, so remove it now
                tombstoneOperations.remove(row.InstanceKey, tableView.serverFolderId);
            }
            else {
                // The row is present in the tombstone operation due to the change in the "Category" property
                // As we shall (and should) not skip processing the notifications for such a row which could be in a view
                // we will use the value of "Category" property from the tombstone map and apply it to the property in payload
                // That ways the state remains in sync with what user expects it to be.
                row.Categories = removeFromTombstoneCondition.value;
            }
            // For categories we stamp the local category value on the payload if it did not match
            // so it is safe to process payload further
            return true;
    }
    return true;
}
/**
 * This function handles rowAdd and rowModified notifications.
 * @param row present in the notification
 * @param tableview for which notification has arrived
 * which we will insert the new conversation payload
 */
export default action('rowNotificationHandler.onAddOrUpdateRow')(function onAddOrUpdateRow(row, tableView) {
    var instanceKey = row.InstanceKey;
    if (!instanceKey) {
        throw new Error('onAddOrUpdateNotification: Row instanceKey should not be null');
    }
    // preProcessRowNotificationPayload, removes the row from tombstone if
    // payload has the same property value as in tombstone for Pin/Category.
    // If not we will not process this payload
    var shouldProcessPayload = preProcessRowNotificationPayload(row, tableView);
    if (!shouldProcessPayload) {
        return;
    }
    // Skip processing the notificaiton if
    // Row is currently delete tombstoned OR (This check is redundant in above, and shall be cleaned up with
    // major tombstone refactor)
    // the table's lastEmptied time is later than the rows delivery time
    if (shouldSkipProcessRow(row, tableView)) {
        return;
    }
    // Compute the insert index by passing in the lastDeliveryOrRenewTimeStamp and lastDeliveryTimeStamp.
    // Cannot use MailRowDataPropertyGetter here because the row hasn't been saved to item cache yet.
    var _a = getDateSortTimeStampFromRowData(row, tableView.tableQuery.listViewType), lastDeliveryOrRenewTimeStamp = _a.lastDeliveryOrRenewTimeStamp, lastDeliveryTimeStamp = _a.lastDeliveryTimeStamp;
    var indexToInsertAt = findIndexToInsertAt(lastDeliveryOrRenewTimeStamp, lastDeliveryTimeStamp, tableView, 0 /* startIndex */);
    var indexOfElement = findIndexByInstanceKey(instanceKey, tableView);
    var isRowAlreadyPresent = indexOfElement >= 0;
    if (isRowAlreadyPresent && indexOfElement < indexToInsertAt) {
        // If we're moving an existing item down in the list, decrease index to insert by 1 to account for the row being
        // removed from above the indexToInsertAt
        indexToInsertAt--;
    }
    if (indexToInsertAt == -1) {
        if (isRowAlreadyPresent) {
            // Remove the row if could not place it, because the row could have moved down (and out of loaded range) for any reason from another client,
            // e.g unpin a row or delete the latest item in the conversation from another client
            removeRowsFromListViewStore([instanceKey], tableView);
        }
        // Ignore the notification if we could not find an index to insert it
        return;
    }
    if (isRowAlreadyPresent) {
        updateExistingRow(row, tableView, indexToInsertAt, indexOfElement);
    }
    else {
        addNewRow(row, tableView, indexToInsertAt);
    }
    // Always prefetch if we're getting a row update because we don't want an entry in our data cache to go stale
    lazyPrefetchSingleRow.importAndExecute(instanceKey, tableView, true /* updateIfExists */);
    // Update focused inbox rollup data for Focused/Other notifications
    if (getFocusedFilterForTable(tableView) != -1 /* None */) {
        updateRollupOnAddOrUpdateRowNotification(tableView);
    }
    if (getUserConfiguration().UserOptions.ShowInlinePreviews) {
        // Get the attachment previews
        attachmentPreviewOperations.getPreviews(getMailboxInfo(tableView), tableView.tableQuery.listViewType);
    }
    if (isControlFeatureEnabled('tri-coupon-peek')) {
        lazyFetchCouponData.import().then(function (fetchCouponData) {
            fetchCouponData(getMailboxInfo(tableView).mailboxId, tableView.tableQuery.listViewType);
        });
    }
});
//# sourceMappingURL=onAddOrUpdateRow.js.map